import { filterNursesForCoverage, INCLUDE_TRAINEES_IN_COVERAGE } from "../../../utils/statsCalculations";
import { formatCount } from "../WeeklyGapView/CountBadge";
import type { ComputedLocation, MissingSlotData, NurseSlot, RoleSection, TimeTitle } from "../WeeklyGapView/useWeeklyGapView";
import { weightedActual } from "../WeeklyGapView/useWeeklyGapView";

/**
 * One person on a shift, as the mobile roster draws them.
 *
 * Carries the `NurseSlot` whole rather than copying fields out of it: the row needs the underlying
 * `NurseInShift` to open the existing nurse menu, and a flattened copy would be a second definition
 * of "who is on this shift" to keep in step.
 */
export type GapsStaffEntry = {
	/** Stable across re-renders and unique within a group — the shift id, which a nurse has one of per group. */
	key: string;
	slot: NurseSlot;
	/** `""` for a shift with no location yet, which the row renders as "Assign unit". */
	locationName: string;
	/**
	 * Whether this person fills a required slot. False for Trainee / Orientation / Education and for a
	 * shift already queued for deletion, exactly as `filterNursesForCoverage` decides it app-wide.
	 * The row still renders — they are a body on the shift — they simply do not move `actual`.
	 */
	countsToCoverage: boolean;
	/**
	 * Whether this person's unit already has more staff than it requires — a unit requiring none included.
	 *
	 * The row says so because it is the one case where the group stops adding up: the header's `actual`
	 * sums every unit, but a unit cannot lend its extra to one that is short, so the missing rows can
	 * outnumber the header's shortfall. It describes the UNIT, not the person — in a unit of three needing
	 * two, nobody is "the extra one", and all three rows truthfully say the unit is over.
	 */
	isInOverParLocation: boolean;
};

/** One missing person, named by the location that is short. One entry per hole, never one per location. */
export type GapsMissingEntry = {
	key: string;
	locationName: string;
	locationShortName: string;
	/** This location's own filled/required, so the row says what it is fixing. */
	filled: number;
	required: number;
	/** The slot's own hours when the config differs from the group's, else the group's label. */
	timeLabel: string;
	/**
	 * The posted request the desktop's matcher placed in this hole, or `null` when none stands against it. Carried
	 * so a tap can open the add sheet LINKED to that request (`completeManualAdd`), as the desktop grid opens it.
	 */
	postedRequestId: string | null;
	/** A posted request already covers this hole — drawn twice, counted once (the artifact says so too). Always `postedRequestId !== null`. */
	hasOpenRequest: boolean;
	/** Slot-specific window, so a tap can pre-fill the destination with the right hours. */
	startMin: number;
	endMin: number;
};

/**
 * One unit in a group's window, with its own counts — what the assign-unit and change-location sheets list.
 *
 * Both numbers are the unit's own `LocationDayData`, the same pair its missing rows print: `filled` is the weighted
 * `weightedActual` sum, never a count of people, so a unit holding a half-overlap reads `0.5/1` here as it does there.
 */
export type GapsLocationEntry = {
	locationName: string;
	locationShortName: string;
	filled: number;
	required: number;
	/**
	 * No config runs in this unit today and nobody is on it — the desktop greys the cell out. Carried rather than
	 * filtered: it is still one of the department's units, and a sheet may place someone there; whether to dim it is
	 * the sheet's call.
	 */
	isBlocked: boolean;
};

/** A job × shift-window group: the header line, its people, and its holes. */
export type GapsShiftGroup = {
	/**
	 * `label|window` (`groupKeyOf`). Unique for the home only once its sections are keyed (`keySections`): the home's
	 * build (`buildScopedGapsDayModel`) keys a group by its SECTION, which tells apart two sections of one name.
	 */
	key: string;
	/** The section's name — a job, a role group or a role, depending on the department's grouping. */
	jobLabel: string;
	/** Whether the section is a job (job management on) rather than a role or role group — decides its colours. */
	isJobSection: boolean;
	/** The section's job, on job sections only — its colour comes from the job, not the label. */
	jobId?: string;
	/** The shift window, e.g. `7a-3p`. The artifact makes this the headline and the job the small print. */
	timeLabel: string;
	startMin: number;
	endMin: number;
	/**
	 * `actual` is the section's own weighted sum, NOT a count of `staff`.
	 *
	 * A 4-hour nurse on an 8-hour window contributes 0.5, and counting cards would report `3/5` where
	 * the desktop weekly view reports `2.5/5`. Taken straight from `TimeTitle.dayTotals` so the two
	 * screens cannot disagree.
	 */
	actual: number;
	required: number;
	staff: GapsStaffEntry[];
	missing: GapsMissingEntry[];
	/**
	 * Every unit in the window, in the order the weekly gap view sorted them. The unassigned pseudo-unit is left
	 * out: it is where a shift with no location lands, not a place anyone can be assigned to.
	 */
	locations: GapsLocationEntry[];
	/** Posted requests standing against this group's holes. */
	openRequestCount: number;
	/**
	 * People on the shift kept out of par by their TRAINING status — the group's "N in orientation or
	 * training" note. A shift queued for removal also does not count (see `countsToCoverage`), but it is not
	 * in training, and the note must not say it is.
	 */
	excludedFromParCount: number;
};

/** Per-job shortfall for the gap line: `Nurse −2 · Aide ✓`. */
export type GapsJobTotal = {
	/** The section's key (`keySections`): the label, unless two sections share it. */
	key: string;
	jobLabel: string;
	/** Same meaning as on `GapsShiftGroup` — decides the job's colour, so its dot matches its group badges. */
	isJobSection: boolean;
	jobId?: string;
	actual: number;
	required: number;
};

/** The saved scope. An absent set means "everything" — never an empty one, which means "nothing". */
export type GapsDayFilter = {
	jobLabels?: ReadonlySet<string>;
	locationNames?: ReadonlySet<string>;
	timeLabels?: ReadonlySet<string>;
};

/**
 * How a staff count is printed anywhere on this screen: the desktop weekly gap view's own `formatCount`.
 *
 * Whole numbers stay whole; anything fractional keeps ONE decimal. `weightedActual` sums fractional
 * contributions, so a half-shift genuinely produces `2.5` — rounding it away would agree with neither
 * the desktop nor the rows underneath. Delegated rather than restated: a local copy printed `2.96` as
 * `3.0` where the desktop prints `3`, which is exactly the disagreement the parity check exists to catch.
 */
export const formatStaffCount = (value: number): string => String(formatCount(value));

/**
 * Whether a shortfall is drawn as short — decided on the value as it PRINTS, not the raw float. Weighted
 * coverage sums fractions (0.6 + 0.3 + 0.1 is 0.9999999999999999), so "1 − that" is 1e-16 > 0 and would
 * otherwise paint a red "−0" beside a count that reads as covered.
 */
export const isShownAsShort = (shortfall: number): boolean => Number(formatCount(shortfall)) > 0;

/**
 * Everyone on the day's shifts, each shift once, in the order the groups list them. A shift that spans two
 * windows is listed under both groups (see `buildShiftsByGroupForDay`); its first listing is the one kept.
 *
 * The call-in picker's list: fed the UNNARROWED day, because the saved scope does not reach call-ins (P2-11) —
 * a supervisor scoped to three carts can still log a call-in for anyone in the building.
 */
export const distinctStaffEntries = (groups: readonly GapsShiftGroup[]): GapsStaffEntry[] => {
	const seen = new Set<string>();
	return groups.flatMap((group) =>
		group.staff.filter((entry) => {
			const shiftId = entry.slot.nurseInShift.id;
			if (seen.has(shiftId)) return false;
			seen.add(shiftId);
			return true;
		}),
	);
};

/** How many people the Staff list holds — each shift once, for the reason `distinctStaffEntries` gives. */
export const countDistinctStaff = (groups: readonly GapsShiftGroup[]): number => distinctStaffEntries(groups).length;

/** The location a shift with no location lands in — `useWeeklyGapView` keys it by the empty name. */
export const UNASSIGNED_LOCATION_NAME = "";

const entryKeyFor = (slot: NurseSlot): string => String(slot.nurseInShift.id);

const missingKeyFor = (locationName: string, timeLabel: string, index: number): string => `${locationName}|${timeLabel}|${index}`;

/** The slot's own label when the config carries one, else the window the group is drawn for. */
const labelForMissingSlot = (missing: MissingSlotData, groupLabel: string): string => missing.timeLabel ?? groupLabel;

/**
 * A unit's own filled/required on one day — the pair its missing rows, its over-par mark and its assign-unit entry
 * all read, so the three cannot disagree. Filled is the weighted `weightedActual` sum the weekly view prints.
 */
const unitCountsFor = (location: ComputedLocation, dayIndex: number): { filled: number; required: number } => {
	const dayData = location.dayData[dayIndex];
	return { filled: weightedActual(dayData.nurses), required: dayData.required };
};

const staffForLocation = (location: ComputedLocation, dayIndex: number): GapsStaffEntry[] => {
	const { filled, required } = unitCountsFor(location, dayIndex);
	// The unassigned pseudo-location requires nothing by construction; its people carry "Assign unit"
	// instead, so it is never "over par".
	const isInOverParLocation = location.locationName !== UNASSIGNED_LOCATION_NAME && filled > required;
	return location.dayData[dayIndex].nurses.map((slot) => ({
		key: entryKeyFor(slot),
		slot,
		locationName: location.locationName,
		// The predicate `weightedActual` itself applies, asked of the shift rather than read off its weight: a
		// regular shift that only grazes the window has weight 0, and must not read as "in training".
		countsToCoverage: filterNursesForCoverage(slot.nurseInShift, INCLUDE_TRAINEES_IN_COVERAGE),
		isInOverParLocation,
	}));
};

const missingForLocation = (location: ComputedLocation, dayIndex: number, groupLabel: string): GapsMissingEntry[] => {
	const { filled, required } = unitCountsFor(location, dayIndex);
	return location.dayData[dayIndex].missingSlotLabels.map((missing, index) => ({
		key: missingKeyFor(location.locationName, groupLabel, index),
		locationName: location.locationName,
		locationShortName: location.locationShortName,
		filled,
		required,
		timeLabel: labelForMissingSlot(missing, groupLabel),
		postedRequestId: missing.postedRequestId,
		hasOpenRequest: missing.postedRequestId !== null,
		startMin: missing.startMin,
		endMin: missing.endMin,
	}));
};

const locationsForTimeTitle = (timeTitle: TimeTitle, dayIndex: number): GapsLocationEntry[] =>
	timeTitle.locations
		.filter((location) => location.locationName !== UNASSIGNED_LOCATION_NAME)
		.map((location) => ({
			locationName: location.locationName,
			locationShortName: location.locationShortName,
			...unitCountsFor(location, dayIndex),
			isBlocked: location.dayData[dayIndex].isBlocked,
		}));

/**
 * A gap-view section with the key its groups and its job total are told apart by (`sectionKey`).
 *
 * `useWeeklyGapView` names a section by its label alone, and two can share one: two enabled jobs of the same display
 * name each get a section of that name (L9). Keyed `label|window`, their groups collided — React keys, the home's
 * open-group map and `findGroupByKey` all took the first, so tapping one window opened both. A label two sections share
 * is told apart by the section's index in the week's UNNARROWED list; a unique label is its own key, so every ordinary
 * group keeps the key (and the test ids) it always had. Attached once, before the saved scope narrows the list —
 * `narrowSections` carries it through — so a group keeps one key whether or not the scope hides its namesake.
 */
export type KeyedRoleSection = RoleSection & { sectionKey: string };

export const keySections = (sections: RoleSection[]): KeyedRoleSection[] => {
	const labels = sections.map((section) => section.shortRole);
	const shared = new Set(labels.filter((label, index) => labels.indexOf(label) !== index));
	return sections.map((section, sectionIndex) => ({
		...section,
		sectionKey: shared.has(section.shortRole) ? `${section.shortRole}#${sectionIndex}` : section.shortRole,
	}));
};

/** A group's key: its section's, and its window. */
export const groupKeyOf = (sectionKey: string, timeLabel: string): string => `${sectionKey}|${timeLabel}`;

/**
 * Applies the saved scope, and ONLY here.
 *
 * Narrowing has to happen before anything reads a total, because dropping locations changes what the
 * group is short by — `TimeTitle.dayTotals` is the sum over every location, so a filtered screen that
 * kept reading it would print the building's numbers over one supervisor's rows. Rather than thread a
 * filter through the model, the shortfall and the job totals, this rebuilds the sections with the
 * excluded locations gone and the totals recomputed, and everything downstream stays filter-blind.
 *
 * The recomputation is not new arithmetic: it is the same `weightedActual` sum over the surviving
 * locations that `useWeeklyGapView` performs to build `dayTotals` in the first place.
 *
 * Unassigned people survive every location filter. They are on the shift and nobody has yet said
 * where — hiding them behind a unit scope would make a person disappear from the only screen that
 * could place them.
 *
 * Generic so a section's own additions (its `sectionKey`) survive the narrowing.
 */
export const narrowSections = <S extends RoleSection>(sections: S[], filter: GapsDayFilter, dayCount: number): S[] => {
	const { jobLabels, locationNames, timeLabels } = filter;
	if (jobLabels === undefined && locationNames === undefined && timeLabels === undefined) return sections;

	return sections
		.filter((section) => jobLabels === undefined || jobLabels.has(section.shortRole))
		.map((section) => ({
			...section,
			timeTitles: section.timeTitles
				.filter((timeTitle) => timeLabels === undefined || timeLabels.has(timeTitle.label))
				.map((timeTitle) => {
					if (locationNames === undefined) return timeTitle;
					const locations = timeTitle.locations.filter(
						(location) => location.locationName === UNASSIGNED_LOCATION_NAME || locationNames.has(location.locationName),
					);
					return {
						...timeTitle,
						locations,
						dayTotals: Array.from({ length: dayCount }, (_, dayIndex) => ({
							actual: locations.reduce((sum, location) => sum + weightedActual(location.dayData[dayIndex].nurses), 0),
							required: locations.reduce((sum, location) => sum + location.dayData[dayIndex].required, 0),
						})),
					};
				})
				.filter((timeTitle) => timeTitle.locations.length > 0),
		}))
		.filter((section) => section.timeTitles.length > 0);
};

/**
 * Flattens the desktop weekly gap view's `RoleSection → TimeTitle → ComputedLocation` tree into the
 * flat list of groups the phone draws, for ONE day.
 *
 * This is the only genuinely new logic on the gaps-first home, and it deliberately adds no arithmetic
 * — every number it emits was computed by `useWeeklyGapView` for the desktop screen and is copied
 * across. It reshapes and nothing else, which is what makes the two screens verifiable against each
 * other: a group header here must equal `dayTotals[dayIndex]` there, to the decimal.
 *
 * Locations keep the order `useWeeklyGapView` sorted them into (the department's manual order, then
 * floor, then name), so the phone and the grid list a shift's units the same way round.
 *
 * A shift with no location lands in a computed location named `""` whose `required` is 0: its people
 * raise the group's `actual` and it produces no missing row. That is the desktop's behaviour, so the
 * missing rows can total MORE than the header's shortfall when somebody is unassigned — the same way a
 * unit over its par does (see `isInOverParLocation`): both raise `actual` without filling any unit's hole. The artifact
 * hides that by absorbing unassigned people into the emptiest location first; we do not, because the
 * absorption is a guess the artifact's own notes call "invisible and sometimes wrong", and because
 * every missing row has to correspond to a real `missingSlotLabels` entry for the daily-gaps parity
 * check to mean anything.
 */
export const buildGapsDayModel = (sections: RoleSection[], dayIndex: number): GapsShiftGroup[] =>
	sections.flatMap((section) =>
		section.timeTitles.map((timeTitle) => {
			const staff = timeTitle.locations.flatMap((location) => staffForLocation(location, dayIndex));
			const missing = timeTitle.locations.flatMap((location) => missingForLocation(location, dayIndex, timeTitle.label));
			const totals = timeTitle.dayTotals[dayIndex];
			return {
				key: groupKeyOf(section.shortRole, timeTitle.label),
				jobLabel: section.shortRole,
				// Absent on the desktop model means "not a job section" — that is how the weekly view reads it too.
				isJobSection: section.isJobSection === true,
				jobId: section.jobId,
				timeLabel: timeTitle.label,
				startMin: timeTitle.startMin,
				endMin: timeTitle.endMin,
				actual: totals.actual,
				required: totals.required,
				staff,
				missing,
				locations: locationsForTimeTitle(timeTitle, dayIndex),
				openRequestCount: missing.filter((entry) => entry.hasOpenRequest).length,
				// `countsToCoverage` is false for two reasons — training status and a pending removal. The note names
				// only the first, so the second is taken out here rather than restating the training statuses.
				excludedFromParCount: staff.filter((entry) => !entry.countsToCoverage && !entry.slot.nurseInShift.isPendingToDelete).length,
			};
		}),
	);

/**
 * One window's shortfall, in staff: how many more it requires than it has, never negative. The single place the
 * clamp lives — the day total sums it, and the cover prompt prints it for one group.
 */
export const shortfallOf = (totals: { actual: number; required: number }): number => Math.max(0, totals.required - totals.actual);

/**
 * The day's shortfall, in staff.
 *
 * Clamped per window BEFORE summing, so an over-staffed morning never cancels a short night. The
 * artifact's `−3` is a shortfall, not a net, and summing signed deltas would quietly report a
 * building as whole while two of its shifts were broken in opposite directions.
 */
export const shortfallForDay = (sections: RoleSection[], dayIndex: number): number =>
	sections.reduce(
		(sectionTotal, section) => sectionTotal + section.timeTitles.reduce((titleTotal, timeTitle) => titleTotal + shortfallOf(timeTitle.dayTotals[dayIndex]), 0),
		0,
	);

/** Every day's shortfall in one pass, for the week strip. `dayCount` is `weekViewData.length`. */
export const shortfallsForWeek = (sections: RoleSection[], dayCount: number): number[] =>
	Array.from({ length: dayCount }, (_, dayIndex) => shortfallForDay(sections, dayIndex));

/**
 * Posted requests the desktop's matcher placed into a hole on this day, across these sections.
 *
 * `useWeeklyGapView` already decides which job, window and unit a posted request belongs to — it runs
 * `createPostedRequestMatcher` and writes the answer onto the hole as `postedRequestId`. Reading it back
 * is how the Posted tab follows the saved scope without a second, disagreeing matcher.
 */
export const postedRequestIdsForDay = (sections: RoleSection[], dayIndex: number): ReadonlySet<string> =>
	new Set(
		sections.flatMap((section) =>
			section.timeTitles.flatMap((timeTitle) =>
				timeTitle.locations.flatMap((location) =>
					location.dayData[dayIndex].missingSlotLabels.flatMap((missing) => (missing.postedRequestId === null ? [] : [missing.postedRequestId])),
				),
			),
		),
	);

/** Which posted requests the whole day places, and which of those survive the saved scope. */
export type GapsPostedScope = { placed: ReadonlySet<string>; inScope: ReadonlySet<string> };

/**
 * Whether a posted request belongs on the Posted tab under the saved scope.
 *
 * A request the matcher placed nowhere — charge, hours the building does not run, more people asked
 * for than there are holes — has no job, unit or window to be scoped by, so it survives every filter,
 * the same way an unassigned person does in `narrowSections`: hiding it would make it vanish from the
 * only list that shows it.
 */
export const isPostedRequestInScope = (requestId: string, scope: GapsPostedScope): boolean => !scope.placed.has(requestId) || scope.inScope.has(requestId);

/**
 * Per-job totals for the gap line, in the order the sections came in.
 *
 * Summed from `dayTotals` rather than from the flattened groups for the same reason the header reads
 * it: the weighted actual is the section's, not a count of the rows under it.
 */
export const buildGapsJobTotals = (sections: KeyedRoleSection[], dayIndex: number): GapsJobTotal[] =>
	sections.map((section) => ({
		key: section.sectionKey,
		jobLabel: section.shortRole,
		isJobSection: section.isJobSection === true,
		jobId: section.jobId,
		actual: section.timeTitles.reduce((sum, timeTitle) => sum + timeTitle.dayTotals[dayIndex].actual, 0),
		required: section.timeTitles.reduce((sum, timeTitle) => sum + timeTitle.dayTotals[dayIndex].required, 0),
	}));
